import { Component, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Message, FeedbackRating } from '../../models/chat.model';
import { SanitizerService } from '../../services/sanitizer.service';
import { SourceCitationComponent } from '../source-citation/source-citation.component';

@Component({
	selector: 'app-message',
	templateUrl: './message.component.html',
	styleUrls: ['./message.component.scss'],
})
export class MessageComponent implements OnDestroy {
	@Input() message!: Message;
	@Input() showLabel = true;
	@Input() isSidecar = false;
	@Input() userQuery = '';
	@Output() openArtifact = new EventEmitter<{ title: string; content: string }>();
	@Output() viewDocument = new EventEmitter<{ title: string; s3FileVersionGUID: string }>();
	@Output() requestFullscreen = new EventEmitter<{ title: string; s3FileVersionGUID: string }>();
	@ViewChild(SourceCitationComponent) sourceCitation?: SourceCitationComponent;
	hovering = false;
	copied = false;

	readonly copyToClipboardAction = () => this.copyToClipboard();

	// Progressive rendering state
	isProgressiveRendering = false;
	progressiveText = '';
	private animationFrameId = 0;
	private fullText = '';
	private charIndex = 0;
	private lastTimestamp = 0;

	constructor(private sanitizer: SanitizerService) {}

	ngOnDestroy(): void {
		this.stopAnimation();
	}

	@HostListener('document:keydown.escape')
	onEscapeKey(): void {
		if (this.isProgressiveRendering) {
			this.skipAnimation();
		}
	}

	startProgressiveRender(text: string): void {
		this.fullText = text;
		this.progressiveText = '';
		this.charIndex = 0;
		this.isProgressiveRendering = true;
		this.lastTimestamp = 0;
		this.animateNextChars(0);
	}

	skipAnimation(): void {
		this.stopAnimation();
		this.progressiveText = this.fullText;
		this.isProgressiveRendering = false;
	}

	private animateNextChars(timestamp: number): void {
		if (!this.isProgressiveRendering) return;

		if (this.lastTimestamp === 0) {
			this.lastTimestamp = timestamp;
		}

		const elapsed = timestamp - this.lastTimestamp;
		// Append characters at ~10ms intervals
		const charsToAdd = Math.max(1, Math.floor(elapsed / 10));

		if (elapsed >= 10 || this.charIndex === 0) {
			const end = Math.min(this.charIndex + charsToAdd, this.fullText.length);
			this.progressiveText = this.fullText.substring(0, end);
			this.charIndex = end;
			this.lastTimestamp = timestamp;
		}

		if (this.charIndex >= this.fullText.length) {
			this.isProgressiveRendering = false;
			this.animationFrameId = 0;
			return;
		}

		this.animationFrameId = requestAnimationFrame((ts) => this.animateNextChars(ts));
	}

	private stopAnimation(): void {
		if (this.animationFrameId) {
			cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = 0;
		}
	}

	get isAssistant(): boolean {
		return this.message.role === 'assistant';
	}

	/**
	 * Get content to display — uses progressiveText during animation, strippedContent otherwise.
	 */
	get displayContent(): string {
		if (this.isProgressiveRendering) {
			return this.progressiveText;
		}
		return this.strippedContent;
	}

	/**
	 * Get sanitized content for AI responses.
	 * Security Fix RS-M-3: DOMPurify sanitization before Markdown rendering.
	 */
	get sanitizedContent(): string {
		if (!this.isAssistant) {
			return this.message.content || '';
		}
		return this.sanitizer.sanitizeAIContent(this.displayContent);
	}

	/** Detect [artifact:Title]...[/artifact] blocks OR auto-detect long structured Direct mode responses */
	get artifactBlocks(): Array<{ title: string; content: string }> {
		if (!this.isAssistant) return [];

		// First check for explicit [artifact:...]...[/artifact] tags
		const regex = /\[artifact:(.*?)\]([\s\S]*?)\[\/artifact\]/g;
		const blocks: Array<{ title: string; content: string }> = [];
		let match;
		while ((match = regex.exec(this.message.content || '')) !== null) {
			blocks.push({ title: match[1].trim(), content: match[2].trim() });
		}
		if (blocks.length > 0) return blocks;

		// No fallback auto-detection — rely on explicit [artifact:] tags from the model.
		// The Direct Agent's system prompt instructs it to wrap documents in artifact tags.
		// Without explicit tags, the response is conversational and should not open a panel.
		return [];
	}

	/** Message content with [form:...] and [artifact:...]...[/artifact] tags removed. */
	get strippedContent(): string {
		return (this.message.content || '')
			.replace(/\[form:[a-zA-Z0-9_-]+\]/g, '')
			.replace(/\[artifact:.*?\][\s\S]*?\[\/artifact\]/g, '')
			.trim();
	}

	get hasSources(): boolean {
		return !!this.message.sources && this.message.sources.length > 0;
	}

	get formattedTime(): string {
		const d = new Date(this.message.timestamp);
		const now = new Date();
		const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
		const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
		if (sameDay) return time;
		const sameYear = d.getFullYear() === now.getFullYear();
		const month = d.toLocaleString('default', { month: 'short' });
		const day = d.getDate();
		if (sameYear) return `${month} ${day}, ${time}`;
		return `${month} ${day}, ${d.getFullYear()}, ${time}`;
	}

	get metadataText(): string {
		const parts = [this.formattedTime];
		if (this.message.metadata?.tokenCount) {
			parts.push(`${this.message.metadata.tokenCount} tokens`);
		}
		if (this.message.metadata?.latency) {
			parts.push(`${(this.message.metadata.latency / 1000).toFixed(1)}s`);
		}
		return parts.join(' \u00B7 ');
	}

	copyToClipboard(): void {
		navigator.clipboard.writeText(this.message.content || '').then(() => {
			this.copied = true;
			setTimeout(() => (this.copied = false), 2000);
		});
	}

	/** Split content into paragraphs and match each to sources */
	get paragraphsWithSources(): Array<{ text: string; sourceIndices: number[] }> {
		if (!this.isAssistant || !this.message.sources?.length) return [];
		const paragraphs = this.message.content.split(/\\n\\n+/).filter((p) => p.trim());
		if (paragraphs.length <= 1) return [];
		return paragraphs.map((para) => {
			const paraWords = new Set(
				para
					.toLowerCase()
					.replace(/[^a-z0-9\\s]/g, '')
					.split(/\\s+/)
					.filter((w) => w.length > 3)
			);
			const sourceIndices: number[] = [];
			this.message.sources!.forEach((source, i) => {
				if (!source.excerpt) return;
				const excerptWords = source.excerpt
					.toLowerCase()
					.replace(/[^a-z0-9\\s]/g, '')
					.split(/\\s+/)
					.filter((w) => w.length > 3);
				const overlap = excerptWords.filter((w) => paraWords.has(w)).length;
				const ratio = excerptWords.length > 0 ? overlap / Math.min(paraWords.size, excerptWords.length) : 0;
				if (ratio > 0.15 && overlap >= 3) sourceIndices.push(i);
			});
			return { text: para, sourceIndices };
		});
	}

	/** Whether to use paragraph-level footnotes */
	get useParagraphFootnotes(): boolean {
		return this.paragraphsWithSources.length > 1 && this.message.sources!.length > 0;
	}

	/** Handle footnote badge click — expand source citations and scroll to the source */
	onFootnoteClick(index: number): void {
		if (this.sourceCitation) {
			if (!this.sourceCitation.expanded) {
				this.sourceCitation.toggleExpanded();
			}
			// Scroll to the source row after a brief delay for expansion animation
			setTimeout(() => {
				const rows = document.querySelectorAll('.source-row');
				if (rows[index]) {
					rows[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
					rows[index].classList.add('footnote-highlight');
					setTimeout(() => rows[index].classList.remove('footnote-highlight'), 2000);
				}
			}, 100);
		}
	}

	onFeedbackSubmitted(feedback: FeedbackRating): void {
		// Update the message object directly so the UI persists the selection
		this.message.feedback = feedback;
	}
}
